import { Component, computed, effect, inject, signal, Signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SupervisorDialogComponent } from '../supervisor-dialog/supervisor-dialog.component';
import {
  LucideAngularModule,
  LayoutDashboard,
  ScanLine,
  ClipboardList,
  FileCheck,
  KeyRound,
  ListChecks,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
} from 'lucide-angular';
import { PermissionService, AuthService, LayoutService, TaskService } from '@core/services';
import { User } from '@core/models';

/**
 * Sidebar (app shell navigation)
 * Aleia-branded, collapsible left nav. Modules are permission-gated.
 */
@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, LucideAngularModule, SupervisorDialogComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent {
  protected readonly icons = {
    LayoutDashboard,
    ScanLine,
    ClipboardList,
    FileCheck,
    KeyRound,
    ListChecks,
    LogOut,
    PanelLeftClose,
    PanelLeftOpen,
    Sparkles,
  };

  /** Inactive/active icon color — bound directly (not via CSS) since
   *  lucide-angular's `color` input doesn't reliably inherit through a
   *  descendant CSS selector across the component boundary. */
  protected navIconColor(active: boolean): string {
    return active ? 'var(--accent)' : '#6b7280';
  }

  protected readonly user: Signal<User | undefined>;
  protected readonly userDisplayName: Signal<string>;
  protected readonly userInitials: Signal<string>;
  protected readonly userRoleLabel: Signal<string>;

  /** Whether the mouse is over the collapsed-rail submark (shows the expand toggle). */
  protected readonly hoverLogo = signal(false);

  protected readonly canAccessOrientador: Signal<boolean>;
  protected readonly canAccessFarmaceuta: Signal<boolean>;
  /** Dispensaciones is everyone's who dispenses, supervises or audits. */
  protected readonly canAccessDispensaciones: Signal<boolean>;
  /** Por dispensar: theirs, and the orientador's, who loads it. */
  protected readonly canAccessPorDispensar: Signal<boolean>;
  protected readonly canAccessAuditoria: Signal<boolean>;
  /** The regente's code: an option of their profile, opened as a dialog. */
  protected readonly canAccessSupervisor: Signal<boolean>;
  protected readonly supervisorOpen = signal(false);
  /** Análisis: the data-analysis agent's chat. */
  protected readonly canAccessAnalisis: Signal<boolean>;

  constructor(
    private readonly _permissionService: PermissionService,
    private readonly _authService: AuthService,
    protected readonly layout: LayoutService,
  ) {
    // Reset the hover state on every entry into the collapsed rail — a click
    // that expands the sidebar removes this button before the native
    // `mouseleave` can fire, which would otherwise leave `hoverLogo` stuck
    // `true` and show the open-icon instead of the submark next time it collapses.
    effect(() => {
      if (this.layout.sidebarCollapsed()) this.hoverLogo.set(false);
    });

    this.user = this._permissionService.user;
    this.userDisplayName = computed(() => this._permissionService.getUserDisplayName());

    this.userInitials = computed(() => {
      const name = this.userDisplayName();
      if (!name || name === 'Usuario') return 'U';
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    });

    this.userRoleLabel = computed(() => {
      const user = this.user();
      if (!user) return 'Usuario';
      if (user.is_superuser) return 'Superusuario';
      if (user.groups && user.groups.length > 0) return user.groups[0];
      return 'Usuario';
    });

    this.canAccessOrientador = computed(() =>
      this._permissionService.canAccessModule('view_orientador_module'),
    );
    this.canAccessFarmaceuta = computed(() =>
      this._permissionService.canAccessModule('view_farmaceuta_module'),
    );
    this.canAccessAuditoria = computed(() =>
      this._permissionService.canAccessModule('view_auditoria_module'),
    );
    this.canAccessSupervisor = computed(() =>
      this._permissionService.canAccessModule('view_supervisor_module'),
    );
    this.canAccessAnalisis = computed(() =>
      this._permissionService.canAccessModule('view_inteligencia_module'),
    );
    this.canAccessDispensaciones = computed(
      () => this.canAccessFarmaceuta() || this.canAccessAuditoria() || this.canAccessSupervisor(),
    );
    this.canAccessPorDispensar = computed(
      () => this.canAccessOrientador() || this.canAccessDispensaciones(),
    );
    // The open tasks' badge: on arrival, and every few minutes after.
    effect(() => {
      if (!this.canAccessDispensaciones()) return;
      void this.taskService.refreshCount();
    });
    setInterval(() => {
      if (this.canAccessDispensaciones()) void this.taskService.refreshCount();
    }, 120_000);
  }

  protected readonly taskService = inject(TaskService);

  protected async logout(): Promise<void> {
    await this._authService.logout$();
  }

  /** Nav-item classes — icon centered with no label in the collapsed rail. */
  protected navItemClass(): string {
    const base = 'nav-item flex items-center rounded-xl py-2.5 no-underline transition-colors';
    return this.layout.sidebarCollapsed() ? `${base} justify-center` : `${base} gap-3 px-3`;
  }
}
